import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import Login from "../modules/login";
import Register from "../modules/register";
import Home from "../modules/home";
import NewPrescription from "../modules/prescriptions/new";
import NewOrder from "../modules/orders/new";
import TermsAndConditions from "../modules/termsAndConditions";
import PrivacyPolicy from "../modules/privacyPolicy";
import { AuthProvider, useAuth } from "../auth/context";
import RequestPasswordRecovery from "../modules/passwordRecovery";
import ChangePassword from "../modules/passwordRecovery/change";
import ResponsiveAppBar from "./ResponsiveAppBar";
import { Stack } from "@mui/material";
import Prescriptions from "../modules/prescriptions";
import NewPatient from "../modules/patients/new";
import Settings from "../modules/settings";
import PatientSelfRegister from "../modules/patientSelfRegister";
import { useAutoSignout } from "../utils/hooks";
import DetailsDocument from "../modules/detailsDocument";
import Patients from "../modules/patients";
import PatientDetail from "../modules/patients/detail";
import ShareRegistrationDialog from "../modules/utils/patient/new/ShareRegistrationDialog";
import DownloadMedicalDocuments from "../modules/medicalDocuments/download";
import NewCombinedMedicalDocument from "../modules/medicalDocuments/new";
import Calculators from "../modules/calculators";
import RouteErrorBoundary from "./RouteErrorBoundary";
import PasswordExpiryDialog from "../utils/passwordExpiry/PasswordExpiryDialog";

const WithAuthProvider = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

const ProtectedRoute = () => {
  const { user } = useAuth();
  const { search } = useLocation();
  useAutoSignout();

  if (!user) return <Navigate to={{ pathname: "/login", search }} replace />;

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "row",
        pt: { xs: 5, md: 0 },
        height: "100%",
        boxSizing: { xs: "border-box" },
      }}
    >
      <ResponsiveAppBar />
      <Outlet />
      <PasswordExpiryDialog />
    </Stack>
  );
};

const DoctorRoute = ({ children }) => {
  const { user } = useAuth();

  if (user.type !== "doctor") return <Navigate to={user.landingRoute()} />;

  return children;
};

const routes = [
  {
    element: <WithAuthProvider />,
    children: [
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "register",
        element: <Register />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/",
            element: (
              <DoctorRoute>
                <Home />
              </DoctorRoute>
            ),
          },
          {
            path: "settings",
            element: (
              <DoctorRoute>
                <Settings />
              </DoctorRoute>
            ),
          },
          {
            path: "prescriptions",
            children: [
              {
                path: "",
                element: <Prescriptions />,
              },
              {
                path: "new",
                element: (
                  <DoctorRoute>
                    <NewPrescription />
                  </DoctorRoute>
                ),
              },
            ],
          },
          {
            path: "orders",
            children: [
              {
                path: "new",
                element: (
                  <DoctorRoute>
                    <NewOrder />
                  </DoctorRoute>
                ),
              },
            ],
          },
          {
            path: "medical-documents",
            children: [
              {
                path: "new",
                element: (
                  <DoctorRoute>
                    <NewCombinedMedicalDocument />
                  </DoctorRoute>
                ),
              },
            ],
          },
          {
            path: "calculators",
            element: (
              <DoctorRoute>
                <Calculators />
              </DoctorRoute>
            ),
          },
          {
            path: "details-document",
            children: [
              {
                path: ":id",
                element: <DetailsDocument />,
              },
            ],
          },
          {
            path: "patients",
            children: [
              {
                path: "",
                element: <Patients />,
              },

              {
                path: ":id",
                element: <PatientDetail />,
              },

              {
                path: "new",
                element: <NewPatient />,
              },
              {
                path: "new/link-self-registration",
                element: <ShareRegistrationDialog />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "termsAndConditions",
    element: <TermsAndConditions />,
  },
  {
    path: "privacyPolicy",
    element: <PrivacyPolicy />,
  },
  {
    path: "passwordRecovery",
    children: [
      {
        path: "request",
        element: <RequestPasswordRecovery />,
      },
      {
        path: "change",
        element: <ChangePassword />,
      },
    ],
  },
  {
    path: "patients/selfRegister",
    element: <PatientSelfRegister />,
  },
  {
    path: "medical-documents/download",
    element: <DownloadMedicalDocuments />,
  },
];

export default createBrowserRouter([
  { errorElement: <RouteErrorBoundary />, children: routes },
]);
